import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import {
  ABSENCE_POLICIES,
  EXEMPTION_POLICIES,
  GRADING_TYPES,
  MISSING_POLICIES,
  MULTI_EVALUATOR_RULES,
  PERFORMANCE_DIRECTIONS,
  PERFORMANCE_UNITS,
  type PerformanceDirection,
  type PerformanceRow,
  type PerformanceUnit,
  type StageDisciplineInfo,
  type StageInfo,
  type StageStatusOption,
} from '@concours/shared'
import { Alert, Button, Input, Modal, Select, useAuth, useToast } from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { SELECTION_KEY, useCatalogue, type CatalogueDiscipline } from '../../lib/selection'
import { StatusEditor } from './StageModal'

const options = (o: Record<string, string>) =>
  Object.entries(o).map(([value, label]) => ({ value, label }))
const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')))

/**
 * Discipline d'une étape (cahier §9.3 à §9.6) : notation, barème, coefficient,
 * seuils, correcteurs, et comportement en cas d'absence / dispense / note
 * manquante — toujours CHOISI (aucune valeur présélectionnée).
 */
export function DisciplineModal({
  stage,
  current,
  onClose,
}: {
  stage: StageInfo
  current: StageDisciplineInfo | null
  onClose: () => void
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const catalogue = useCatalogue()
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({ code: '', label: '' })
  const [form, setForm] = useState({
    disciplineId: current?.disciplineId ?? '',
    grading: current?.grading ?? 'numeric',
    minScore: String(current?.minScore ?? 0),
    maxScore: String(current?.maxScore ?? 20),
    coefficient: String(current?.coefficient ?? 1),
    eliminatoryThreshold:
      current?.eliminatoryThreshold == null ? '' : String(current.eliminatoryThreshold),
    precision: String(current?.precision ?? 0.25),
    evaluators: String(current?.evaluators ?? 1),
    multiRule: current?.multiRule ?? 'average',
    maxGap: current?.maxGap == null ? '' : String(current.maxGap),
    absence: current?.absence ?? '',
    exemption: current?.exemption ?? '',
    missing: current?.missing ?? '',
    compensable: current?.compensable ?? true,
    unit: current?.scale?.unit ?? 'seconds',
    direction: current?.scale?.direction ?? 'lower_better',
  })
  const [rows, setRows] = useState<PerformanceRow[]>(
    current?.scale?.rows ?? [
      { gender: null, performance: 0, score: 20 },
      { gender: null, performance: 0, score: 0 },
    ]
  )
  const [statuses, setStatuses] = useState<StageStatusOption[]>(
    current?.statuses ?? [
      { code: 'apte', label: 'Apte', passes: true },
      { code: 'non_apte', label: 'Non apte', passes: false },
    ]
  )
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))
  const used = new Set(stage.disciplines.map((d) => d.disciplineId))

  const createDiscipline = useMutation({
    mutationFn: () =>
      session.api.post<CatalogueDiscipline>('/admin/disciplines', {
        code: draft.code.trim().toUpperCase(),
        label: draft.label.trim(),
      }),
    onSuccess: async (row) => {
      await queryClient.invalidateQueries({ queryKey: [...SELECTION_KEY, 'disciplines'] })
      set({ disciplineId: row.id })
      setCreating(false)
      setDraft({ code: '', label: '' })
      toast.success(`Discipline « ${row.label} » ajoutée au catalogue`)
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const save = useMutation({
    mutationFn: () => {
      const body = {
        disciplineId: form.disciplineId,
        grading: form.grading,
        minScore: num(form.minScore) ?? 0,
        maxScore: num(form.maxScore) ?? 0,
        coefficient: num(form.coefficient) ?? 0,
        eliminatoryThreshold: num(form.eliminatoryThreshold),
        precision: num(form.precision) ?? 0,
        evaluators: Number(form.evaluators),
        multiRule: form.multiRule,
        maxGap: num(form.maxGap),
        absence: form.absence,
        exemption: form.exemption,
        missing: form.missing,
        compensable: form.compensable,
        scale:
          form.grading === 'performance'
            ? { unit: form.unit, direction: form.direction, rows }
            : null,
        statuses: form.grading === 'status' ? statuses : null,
      }
      return current
        ? session.api.put(`/admin/stages/${stage.id}/disciplines/${current.id}`, body)
        : session.api.post(`/admin/stages/${stage.id}/disciplines`, body)
    },
    onSuccess: async () => {
      toast.success(current ? 'Discipline enregistrée' : 'Discipline ajoutée à l’étape')
      await queryClient.invalidateQueries({ queryKey: SELECTION_KEY })
      onClose()
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(save.error)
  const numeric = form.grading !== 'status'
  const ready = form.disciplineId && form.absence && form.exemption && form.missing
  const available = (catalogue.data ?? []).filter(
    (d) => (d.isActive && !used.has(d.id)) || d.id === current?.disciplineId
  )

  return (
    <Modal
      open
      size="xl"
      onClose={onClose}
      title={
        current ? `${current.label} — ${stage.label}` : `Ajouter une discipline — ${stage.label}`
      }
      description="Chaque discipline a sa propre notation. Les comportements en cas d’absence, de dispense ou de note manquante doivent être choisis explicitement."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!ready}
            onClick={() => save.mutate()}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-5)' }}>
        <section className="stack" style={{ gap: 'var(--space-3)' }}>
          <h3 className="sel-section-title">Discipline</h3>
          <div className="form-grid">
            <Select
              label="Discipline du catalogue"
              required
              value={form.disciplineId}
              placeholder="— Choisir —"
              error={errors.disciplineId}
              disabled={Boolean(current)}
              options={available.map((d) => ({ value: d.id, label: `${d.label} (${d.code})` }))}
              onChange={(e) => set({ disciplineId: e.target.value })}
            />
            <Select
              label="Type de notation"
              required
              value={form.grading}
              options={options(GRADING_TYPES)}
              onChange={(e) => set({ grading: e.target.value as typeof form.grading })}
            />
          </div>
          {!current &&
            (creating ? (
              <div
                className="row"
                style={{ gap: 'var(--space-2)', alignItems: 'end', flexWrap: 'wrap' }}
              >
                <Input
                  label="Code"
                  value={draft.code}
                  hint="Majuscules, chiffres, _ (ex. CULT_GEN)"
                  onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
                />
                <Input
                  label="Libellé"
                  value={draft.label}
                  onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                />
                <Button
                  loading={createDiscipline.isPending}
                  disabled={draft.code.trim().length < 2 || draft.label.trim().length < 2}
                  onClick={() => createDiscipline.mutate()}
                >
                  Créer
                </Button>
                <Button variant="ghost" onClick={() => setCreating(false)}>
                  Annuler
                </Button>
              </div>
            ) : (
              <div>
                <Button size="sm" variant="ghost" icon={<Plus />} onClick={() => setCreating(true)}>
                  Nouvelle discipline au catalogue
                </Button>
              </div>
            ))}
        </section>

        {numeric && (
          <section className="stack" style={{ gap: 'var(--space-3)' }}>
            <h3 className="sel-section-title">Barème et pondération</h3>
            <div className="form-grid form-grid-3">
              <Input
                label="Note minimale"
                required
                inputMode="decimal"
                value={form.minScore}
                error={errors.minScore}
                onChange={(e) => set({ minScore: e.target.value })}
              />
              <Input
                label="Note maximale"
                required
                inputMode="decimal"
                value={form.maxScore}
                error={errors.maxScore}
                onChange={(e) => set({ maxScore: e.target.value })}
              />
              <Input
                label="Coefficient"
                required
                inputMode="decimal"
                value={form.coefficient}
                error={errors.coefficient}
                onChange={(e) => set({ coefficient: e.target.value })}
              />
              <Input
                label="Note éliminatoire"
                inputMode="decimal"
                value={form.eliminatoryThreshold}
                error={errors.eliminatoryThreshold}
                hint="Facultatif : en dessous, le candidat est éliminé."
                onChange={(e) => set({ eliminatoryThreshold: e.target.value })}
              />
              <Input
                label="Pas de saisie"
                required
                inputMode="decimal"
                value={form.precision}
                error={errors.precision}
                hint="Ex. 0,25 ; 0,5 ; 1"
                onChange={(e) => set({ precision: e.target.value })}
              />
              <label className="check-row" style={{ alignSelf: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.compensable}
                  onChange={(e) => set({ compensable: e.target.checked })}
                />
                <span>Compensable par les autres disciplines</span>
              </label>
            </div>
          </section>
        )}

        {form.grading === 'performance' && (
          <ScaleEditor
            unit={form.unit}
            direction={form.direction}
            rows={rows}
            error={errors.scale}
            onUnit={(unit) => set({ unit })}
            onDirection={(direction) => set({ direction })}
            onRows={setRows}
          />
        )}
        {form.grading === 'status' && (
          <StatusEditor statuses={statuses} onChange={setStatuses} error={errors.statuses} />
        )}

        <section className="stack" style={{ gap: 'var(--space-3)' }}>
          <h3 className="sel-section-title">Correction</h3>
          <div className="form-grid form-grid-3">
            <Select
              label="Nombre de correcteurs"
              value={form.evaluators}
              options={[
                { value: '1', label: '1 correcteur' },
                { value: '2', label: '2 correcteurs' },
                { value: '3', label: '3 correcteurs' },
              ]}
              onChange={(e) => set({ evaluators: e.target.value })}
            />
            <Select
              label="Note retenue"
              disabled={form.evaluators === '1'}
              value={form.multiRule}
              options={options(MULTI_EVALUATOR_RULES)}
              onChange={(e) => set({ multiRule: e.target.value as typeof form.multiRule })}
            />
            <Input
              label="Écart déclenchant une 3e correction"
              inputMode="decimal"
              disabled={form.multiRule !== 'max_gap' || form.evaluators === '1'}
              value={form.maxGap}
              error={errors.maxGap}
              onChange={(e) => set({ maxGap: e.target.value })}
            />
          </div>
        </section>

        <section className="stack" style={{ gap: 'var(--space-3)' }}>
          <h3 className="sel-section-title">Cas particuliers</h3>
          <Alert tone="info">
            Aucun comportement n’est présélectionné : il doit refléter le règlement du concours.
          </Alert>
          <div className="form-grid form-grid-3">
            <Select
              label="Candidat absent"
              required
              placeholder="— Choisir —"
              value={form.absence}
              error={errors.absence}
              options={options(ABSENCE_POLICIES)}
              onChange={(e) => set({ absence: e.target.value as typeof form.absence })}
            />
            <Select
              label="Candidat dispensé"
              required
              placeholder="— Choisir —"
              value={form.exemption}
              error={errors.exemption}
              options={options(EXEMPTION_POLICIES)}
              onChange={(e) => set({ exemption: e.target.value as typeof form.exemption })}
            />
            <Select
              label="Note manquante au calcul"
              required
              placeholder="— Choisir —"
              value={form.missing}
              error={errors.missing}
              options={options(MISSING_POLICIES)}
              onChange={(e) => set({ missing: e.target.value as typeof form.missing })}
            />
          </div>
        </section>
      </div>
    </Modal>
  )
}

/** Barème de performance : correspondance performance → note, commune ou par sexe. */
function ScaleEditor({
  unit,
  direction,
  rows,
  error,
  onUnit,
  onDirection,
  onRows,
}: {
  unit: PerformanceUnit
  direction: PerformanceDirection
  rows: PerformanceRow[]
  error?: string
  onUnit: (u: PerformanceUnit) => void
  onDirection: (d: PerformanceDirection) => void
  onRows: (r: PerformanceRow[]) => void
}) {
  const update = (i: number, patch: Partial<PerformanceRow>) =>
    onRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  return (
    <section className="stack" style={{ gap: 'var(--space-3)' }}>
      <h3 className="sel-section-title">Barème de performance</h3>
      <div className="form-grid">
        <Select
          label="Unité"
          value={unit}
          options={options(PERFORMANCE_UNITS)}
          onChange={(e) => onUnit(e.target.value as PerformanceUnit)}
        />
        <Select
          label="Sens"
          value={direction}
          options={options(PERFORMANCE_DIRECTIONS)}
          onChange={(e) => onDirection(e.target.value as PerformanceDirection)}
        />
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="tbl-wrap">
        <table className="tbl scale-table">
          <caption className="sr-only">Correspondance performance → note</caption>
          <thead>
            <tr>
              <th scope="col">Sexe</th>
              <th scope="col">Performance</th>
              <th scope="col">Note</th>
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>
                  <select
                    className="select"
                    aria-label={`Sexe, ligne ${i + 1}`}
                    value={r.gender ?? ''}
                    onChange={(e) =>
                      update(i, { gender: (e.target.value || null) as PerformanceRow['gender'] })
                    }
                  >
                    <option value="">Tous</option>
                    <option value="F">Femmes</option>
                    <option value="M">Hommes</option>
                  </select>
                </td>
                <td>
                  <input
                    className="input"
                    inputMode="decimal"
                    aria-label={`Performance, ligne ${i + 1}`}
                    value={String(r.performance)}
                    onChange={(e) => update(i, { performance: num(e.target.value) ?? 0 })}
                  />
                </td>
                <td>
                  <input
                    className="input"
                    inputMode="decimal"
                    aria-label={`Note, ligne ${i + 1}`}
                    value={String(r.score)}
                    onChange={(e) => update(i, { score: num(e.target.value) ?? 0 })}
                  />
                </td>
                <td>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    icon={<Trash2 />}
                    aria-label={`Retirer la ligne ${i + 1}`}
                    disabled={rows.length <= 2}
                    onClick={() => onRows(rows.filter((_, j) => j !== i))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <Button
          size="sm"
          icon={<Plus />}
          disabled={rows.length >= 200}
          onClick={() =>
            onRows([...rows, { gender: rows.at(-1)?.gender ?? null, performance: 0, score: 0 }])
          }
        >
          Ajouter une ligne
        </Button>
      </div>
    </section>
  )
}
